/**
 * React Query Client Configuration
 *
 * Centralized QueryClient instance with default configuration
 * Integrates with error handling and notification systems
 */

import { QueryClient, DefaultOptions } from '@tanstack/react-query';
import { handleError } from '../../core/error-handling';
import { showNotification } from '../../core/notifications';
import {
  DEFAULT_CACHE_TIME,
  DEFAULT_STALE_TIME,
  DEFAULT_RETRY_COUNT,
  DEFAULT_RETRY_DELAY,
  REFETCH_ON_WINDOW_FOCUS,
  REFETCH_ON_RECONNECT,
  REFETCH_ON_MOUNT,
  NETWORK_MODE,
} from './constants';

// ============================================================================
// DEFAULT OPTIONS
// ============================================================================

/**
 * Default options for all queries and mutations
 */
const defaultOptions: DefaultOptions = {
  queries: {
    // Cache configuration
    gcTime: DEFAULT_CACHE_TIME, // Previously cacheTime in v4
    staleTime: DEFAULT_STALE_TIME,

    // Retry configuration
    retry: DEFAULT_RETRY_COUNT,
    retryDelay: DEFAULT_RETRY_DELAY,

    // Refetch configuration
    refetchOnWindowFocus: REFETCH_ON_WINDOW_FOCUS,
    refetchOnReconnect: REFETCH_ON_RECONNECT,
    refetchOnMount: REFETCH_ON_MOUNT,

    // Network mode
    networkMode: NETWORK_MODE,

    // Error handling
    throwOnError: false, // Handle errors manually

    // Suspense (disabled by default)
    suspense: false,
  },

  mutations: {
    // Retry configuration (disabled by default for mutations)
    retry: 0,

    // Network mode
    networkMode: NETWORK_MODE,

    // Error handling
    throwOnError: false,

    // Global mutation handlers
    onError: (error: any) => {
      // Handle mutation errors globally
      handleError(error, {
        context: 'mutation',
        showNotification: true,
      });
    },
  },
};

// ============================================================================
// QUERY CLIENT INSTANCE
// ============================================================================

/**
 * Global QueryClient instance
 *
 * Single instance used throughout the application
 * Configured with default options and error handling
 */
export const queryClient = new QueryClient({
  defaultOptions,
});

// ============================================================================
// QUERY CLIENT UTILITIES
// ============================================================================

/**
 * Reset query client (useful for logout)
 */
export const resetQueryClient = (): void => {
  queryClient.clear();
};

/**
 * Invalidate all queries
 */
export const invalidateAllQueries = async (): Promise<void> => {
  await queryClient.invalidateQueries();
};

/**
 * Refetch all active queries
 */
export const refetchAllQueries = async (): Promise<void> => {
  await queryClient.refetchQueries({ type: 'active' });
};

/**
 * Clear all query cache
 */
export const clearQueryCache = (): void => {
  queryClient.clear();
};

/**
 * Get query data from cache
 */
export const getQueryData = <T = unknown>(queryKey: any[]): T | undefined => {
  return queryClient.getQueryData<T>(queryKey);
};

/**
 * Set query data in cache
 */
export const setQueryData = <T = unknown>(queryKey: any[], data: T): void => {
  queryClient.setQueryData<T>(queryKey, data);
};

/**
 * Invalidate queries by key
 */
export const invalidateQueries = async (queryKey: any[]): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey });
};

/**
 * Refetch queries by key
 */
export const refetchQueries = async (queryKey: any[]): Promise<void> => {
  await queryClient.refetchQueries({ queryKey });
};

/**
 * Remove queries from cache
 */
export const removeQueries = (queryKey: any[]): void => {
  queryClient.removeQueries({ queryKey });
};

/**
 * Prefetch query
 */
export const prefetchQuery = async <T = unknown>(
  queryKey: any[],
  queryFn: () => Promise<T>,
  options?: { staleTime?: number }
): Promise<void> => {
  await queryClient.prefetchQuery({
    queryKey,
    queryFn,
    staleTime: options?.staleTime,
  });
};

/**
 * Check if query exists in cache
 */
export const hasQueryData = (queryKey: any[]): boolean => {
  return queryClient.getQueryData(queryKey) !== undefined;
};

/**
 * Get query state
 */
export const getQueryState = (queryKey: any[]) => {
  return queryClient.getQueryState(queryKey);
};

/**
 * Cancel queries (useful for cleanup)
 */
export const cancelQueries = async (queryKey?: any[]): Promise<void> => {
  await queryClient.cancelQueries({ queryKey });
};

// ============================================================================
// MUTATION UTILITIES
// ============================================================================

/**
 * Get mutation cache
 */
export const getMutationCache = () => {
  return queryClient.getMutationCache();
};

/**
 * Clear mutation cache
 */
export const clearMutationCache = (): void => {
  queryClient.getMutationCache().clear();
};

// ============================================================================
// OPTIMISTIC UPDATE HELPERS
// ============================================================================

/**
 * Perform optimistic update
 *
 * @example
 * ```typescript
 * const context = await optimisticUpdate(
 *   ['todos'],
 *   (oldData) => [...oldData, newTodo]
 * );
 * ```
 */
export const optimisticUpdate = async <T>(
  queryKey: any[],
  updater: (oldData: T | undefined) => T
): Promise<{ previousData: T | undefined }> => {
  // Cancel outgoing refetches
  await queryClient.cancelQueries({ queryKey });

  // Snapshot previous value
  const previousData = queryClient.getQueryData<T>(queryKey);

  // Optimistically update
  queryClient.setQueryData<T>(queryKey, updater(previousData));

  // Return context with rollback data
  return { previousData };
};

/**
 * Rollback optimistic update
 */
export const rollbackOptimisticUpdate = <T>(queryKey: any[], previousData: T): void => {
  queryClient.setQueryData<T>(queryKey, previousData);
};

// ============================================================================
// BATCH UPDATES
// ============================================================================

/**
 * Batch multiple query updates
 * Prevents unnecessary re-renders
 */
export const batchQueryUpdates = (updates: Array<() => void>): void => {
  queryClient.getQueryCache().batch(() => {
    updates.forEach((update) => update());
  });
};

// ============================================================================
// BACKGROUND FETCH
// ============================================================================

/**
 * Fetch query in background (won't show loading states)
 */
export const backgroundFetch = async <T>(
  queryKey: any[],
  queryFn: () => Promise<T>
): Promise<T> => {
  return await queryClient.fetchQuery({
    queryKey,
    queryFn,
    staleTime: 0,
  });
};

// ============================================================================
// PERSISTENCE HELPERS
// ============================================================================

/**
 * Serialize query cache for persistence
 */
export const serializeQueryCache = () => {
  const cache = queryClient.getQueryCache();
  return {
    queries: cache.getAll().map((query) => ({
      queryKey: query.queryKey,
      state: query.state,
    })),
  };
};

/**
 * Restore query cache from serialized data
 */
export const restoreQueryCache = (serialized: any): void => {
  serialized.queries?.forEach((query: any) => {
    queryClient.setQueryData(query.queryKey, query.state.data);
  });
};

// ============================================================================
// QUERY OBSERVERS
// ============================================================================

/**
 * Subscribe to query changes
 */
export const subscribeToQuery = (
  queryKey: any[],
  callback: (data: any) => void
): (() => void) => {
  const cache = queryClient.getQueryCache();

  const unsubscribe = cache.subscribe((event) => {
    if (event?.query.queryKey === queryKey && event?.type === 'updated') {
      callback(event.query.state.data);
    }
  });

  return unsubscribe;
};

// ============================================================================
// DEBUGGING UTILITIES
// ============================================================================

/**
 * Log all queries in cache (development only)
 */
export const logQueries = (): void => {
  if (process.env.NODE_ENV === 'development') {
    const queries = queryClient.getQueryCache().getAll();
    console.group('📊 React Query Cache');
    console.table(
      queries.map((query) => ({
        key: JSON.stringify(query.queryKey),
        state: query.state.status,
        dataUpdatedAt: new Date(query.state.dataUpdatedAt).toLocaleString(),
        isFetching: query.state.fetchStatus === 'fetching',
      }))
    );
    console.groupEnd();
  }
};

/**
 * Log query stats
 */
export const logQueryStats = (): void => {
  if (process.env.NODE_ENV === 'development') {
    const queries = queryClient.getQueryCache().getAll();
    const stats = {
      total: queries.length,
      fetching: queries.filter((q) => q.state.fetchStatus === 'fetching').length,
      success: queries.filter((q) => q.state.status === 'success').length,
      error: queries.filter((q) => q.state.status === 'error').length,
      stale: queries.filter((q) => q.isStale()).length,
    };
    console.log('📊 Query Stats:', stats);
  }
};
